import { apm } from "@elastic/apm-rum";
import { IconButton, Tooltip } from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import {
  Straighten,
  StraightenActive,
} from "shared/common/utils/icons/measurementIcons";
import measurementTools, {
  MeasurementType,
} from "shared/common/utils/measurements/measurementTools";
import PopoverBox from "shared/common/utils/popover/PopoverBox";
import MeasurementContent from "./MeasurementContent";
import SmallToolContent from "./SmallToolContent";
import { mm } from "@inovitas/infra3dsdk";
import { defaultStore } from "../../../../common/stores/defaultStore";

export default function Measurements() {
  const { t } = useTranslation();
  const tools = measurementTools();

  const viewer = defaultStore((state) => state.viewer);
  const manager = defaultStore((state) => state.manager);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);
  const clientConfig = defaultStore((state) => state.clientConfig);

  const measurementPopover = sidebarStore((state) => state.measurementPopover);
  const changeMeasurementPopover = sidebarStore(
    (state) => state.changeMeasurementPopover
  );
  const lastMeasurementActiveTool = sidebarStore(
    (state) => state.lastMeasurementActiveTool
  );
  const changeMeasurementActiveTool = sidebarStore(
    (state) => state.changeMeasurementActiveTool
  );
  const showSmallToolContent = sidebarStore(
    (state) => state.showSmallToolContent
  );
  const closeAllToolPopover = sidebarStore(
    (state) => state.closeAllToolPopover
  );
  const changeCoordinateTool = sidebarStore(
    (state) => state.changeCoordinateTool
  );
  const changeDistanceTool = sidebarStore((state) => state.changeDistanceTool);
  const changeAreaTool = sidebarStore((state) => state.changeAreaTool);
  const changeVerticalDistanceTool = sidebarStore(
    (state) => state.changeVerticalDistanceTool
  );
  const changeOrthogonalDistanceTool = sidebarStore(
    (state) => state.changeOrthogonalDistanceTool
  );
  const changeProfileTool = sidebarStore((state) => state.changeProfileTool);
  const changeLinrefTool = sidebarStore((state) => state.changeLinrefTool);
  const changeSurveyWheelTool = sidebarStore(
    (state) => state.changeSurveyWheelTool
  );
  const changeCoordinateToolValues = sidebarStore(
    (state) => state.changeCoordinateToolValues
  );
  const changeDistanceToolValues = sidebarStore(
    (state) => state.changeDistanceToolValues
  );
  const changeAreaToolValues = sidebarStore(
    (state) => state.changeAreaToolValues
  );
  const changeVerticalDistanceToolValues = sidebarStore(
    (state) => state.changeVerticalDistanceToolValues
  );
  const changeOrthogonalDistanceToolValues = sidebarStore(
    (state) => state.changeOrthogonalDistanceToolValues
  );
  const changeProfileToolValues = sidebarStore(
    (state) => state.changeProfileToolValues
  );
  const changeLinrefToolValues = sidebarStore(
    (state) => state.changeLinrefToolValues
  );
  const changeSurveyWheelToolValues = sidebarStore(
    (state) => state.changeSurveyWheelToolValues
  );

  //change table button and popover
  const handleClickMeasurement = () => {
    closeAllToolPopover();
    changeMeasurementPopover(!measurementPopover);
  };

  //change measure icon if active measure tool is different from cursor
  const icon = () => {
    if (lastMeasurementActiveTool) {
      if (measurementPopover) {
        return lastMeasurementActiveTool.filledIcon;
      } else {
        return lastMeasurementActiveTool.icon;
      }
    } else {
      if (measurementPopover) {
        return <StraightenActive />;
      } else {
        return <Straighten />;
      }
    }
  };

  //save measure distance values on finished
  const onFinished = (info: any) => {
    changeDistanceToolValues(info);
  };
  const onContinousDraw = (info: any) => {
    changeDistanceToolValues(info);
  };

  const distOptions = {
    finishedcbf: onFinished,
    resetcbf: () => changeDistanceToolValues(undefined),
    continousdrawcbf: onContinousDraw,
  };

  const surveyWheelOptions = {
    pointsetcbf: (_: PointerEvent, len: number) => {
      changeSurveyWheelToolValues(len);
    },
    resetcbf: () => changeSurveyWheelTool(undefined),
    continousdrawcbf: (value: number) => {
      if (value !== null) changeSurveyWheelToolValues(value);
    },
    axislayerstyle: new mm.FeatureStyle({
      stylename: "tool_draw",
      rules: [
        {
          filter: "",
          symbolizer: "point",
          params: {
            "font-marker": {
              name: "circle-o",
            },
            size: 10,
            stroke: [1, 0.4, 0, 0.8],
          },
        },
        {
          filter: "",
          symbolizer: "line",
          params: {
            "stroke-width": 1.5,
            stroke: [1, 0.4, 0, 0.8],
            "stroke-dash-width": 4,
            "stroke-gap-width": 2,
          },
        },
      ],
    }),

    highlightstyle: new mm.FeatureStyle({
      stylename: "highlight",
      rules: [
        {
          filter: "name==precise",
          symbolizer: "line",
          params: {
            "stroke-width": 5,
            stroke: [1, 0.4, 0, 1],
          },
        },
        {
          filter: "name==predict",
          symbolizer: "line",
          params: {
            "stroke-width": 5,
            stroke: [1, 0.4, 0, 1],
            "stroke-dash-width": 3,
            "stroke-gap-width": 2,
          },
        },
        {
          filter: "",
          symbolizer: "point",
          params: {
            "font-marker": {
              name: "circle-o",
            },
            size: 10,
            stroke: [1, 0.4, 0, 1],
          },
        },
      ],
    }),
  };

  //save measure vertical values on finished
  const resetProfile = () => {
    changeProfileToolValues(undefined);
  };
  const displayHeight = (info: any) => {
    changeVerticalDistanceToolValues(info);
  };
  const error = (msg: string) => {
    if (msg === "tool_reset_key_changed")
      changeAlertMessage({
        message: t(`alert_info.tool_reset_key_changed`),
        type: "info",
      });
  };
  const verticalDistOptions = {
    finishedcbf: displayHeight,
    resetcbf: () => changeVerticalDistanceToolValues(undefined),
    errorcbf: error,
    continousdraw: true,
    maxdist: 30,
  };

  //save measure area values on finished
  const onFinishedArea = (info: any) => {
    changeAreaToolValues(info);
  };
  const onContinousDrawArea = (info: any) => {
    changeAreaToolValues(info);
  };
  const areaOptions = {
    finishedcbf: onFinishedArea,
    resetcbf: () => changeAreaToolValues(undefined),
    continousdrawcbf: onContinousDrawArea,
    maxdist: 30,
  };

  //save measure coordinates values on finished
  const displayPosition = (info: any) => {
    changeCoordinateToolValues(info);
  };
  const coordinateOptions = {
    finishedcbf: displayPosition,
    errorcbf: error,
    resetcbf: () => changeCoordinateToolValues(undefined),
    continousdraw: true,
    finishinput: mm.USERINPUTTYPE["LEFTCLICK"],
  };

  //save measure profile values on finished
  const profileFinished = (
    pointArray: { x: number; y: number; z: number }[]
  ) => {
    pointArray = pointArray.map((point) => {
      return {
        x: Math.round(point.x * 1000) / 1000,
        y: Math.round(point.y * 1000) / 1000,
        z: Math.round(point.z * 1000) / 1000,
      };
    });
    changeProfileToolValues(pointArray);
  };
  const profileOptions = {
    profilefinishedcbf: profileFinished,
    errorcbf: error,
    resetcbf: resetProfile,
    continousdraw: true,
    maxdist: 30,
  };

  const orthogonalOptions = {
    continousdrawcbf: (info: any) => changeOrthogonalDistanceToolValues(info),
    resetcbf: () => changeOrthogonalDistanceToolValues(undefined),
    finishedcbf: (info: any) => changeOrthogonalDistanceToolValues(info),
  };

  const linrefOptions = {
    maxdist: 50,
    resetcbf: () => changeLinrefToolValues(null),
    finishedcbf: (info: any) => changeLinrefToolValues(info?.uvwCoords),
    continousdrawcbf: (info: any) => changeLinrefToolValues(info), // TODO: performance issue with this
    errorcbf: (msg: string) => {
      console.error(msg);
    },
  };

  //create options for all measurements tools
  useEffect(() => {
    if (viewer === undefined) return;
    const toolHandler = viewer.getScene().getToolHandler();

    const coordinate = toolHandler.createDrawPointTool(
      coordinateOptions,
      "pointMeasure",
      "measure"
    );
    changeCoordinateTool(coordinate);

    const distance = toolHandler.createDrawLineStringTool(
      distOptions,
      "lengthMeasure",
      "measure"
    );
    changeDistanceTool(distance);

    const surveyWheel = toolHandler.createSurveyWheelMeasureTool(
      surveyWheelOptions,
      "surveyWheel",
      "measure"
    );
    changeSurveyWheelTool(surveyWheel);

    const area = toolHandler.createDrawPolygonTool(
      areaOptions,
      "areaMeasure",
      "measure"
    );
    changeAreaTool(area);

    const verticalDistance = toolHandler.createElevationTool(
      verticalDistOptions,
      "verticalDistance",
      "measure"
    );
    changeVerticalDistanceTool(verticalDistance);

    const profile = toolHandler.createProfileTool(
      profileOptions,
      "profile",
      "measure"
    );
    changeProfileTool(profile);

    const orthogonalDistance = toolHandler.createOrthogonalMeasurementTool(
      orthogonalOptions,
      "orthogonalDistance",
      "measure"
    );
    changeOrthogonalDistanceTool(orthogonalDistance);

    const linref = toolHandler.createDrawLRPointTool(
      linrefOptions,
      "linRefMeasure",
      "measure"
    );
    changeLinrefTool(linref);

    if (manager && clientConfig) manager.setViewer(viewer, clientConfig);

    return () => {
      const toolHandler = viewer.getToolHandler();
      if (coordinate) {
        coordinate.disable();
        toolHandler.removeTool(coordinate);
      }
      if (distance) {
        distance.disable();
        toolHandler.removeTool(distance);
      }
      if (surveyWheel) {
        surveyWheel.disable();
        toolHandler.removeTool(surveyWheel);
      }
      if (area) {
        area.disable();
        toolHandler.removeTool(area);
      }
      if (verticalDistance) {
        verticalDistance.disable();
        toolHandler.removeTool(verticalDistance);
      }
      if (profile) {
        profile.disable();
        toolHandler.removeTool(profile);
      }
      if (orthogonalDistance) {
        orthogonalDistance.disable();
        toolHandler.removeTool(orthogonalDistance);
      }
      if (linref) {
        linref.disable();
        toolHandler.removeTool(linref);
      }
    };
  }, [viewer]);

  //when measurement is closed and small tool content closed, remove active measure tool
  useEffect(() => {
    if (!measurementPopover && !showSmallToolContent) {
      changeMeasurementActiveTool(undefined);
    }
  }, [measurementPopover, showSmallToolContent]);

  return (
    <>
      <Tooltip
        title={t("tooltip.measurement")}
        placement="right"
        enterDelay={400}
        classes={{
          tooltip: "infra3d-buttonDescription",
        }}
      >
        <IconButton
          className="onboarding-measurement"
          id="infra3d-measurementButton"
          sx={{
            width: "40px",
            height: "40px",
            overflow: "hidden",
            mx: "auto",
            color: "white",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
          classes={{
            root: measurementPopover
              ? "infra3d-iconButtonRippleActive"
              : "infra3d-iconButtonRipple",
          }}
          onClick={() =>
            monitor(
              apm,
              "change-status-measure | Toolbar",
              handleClickMeasurement
            )
          }
          disableRipple={true}
        >
          {icon()}
        </IconButton>
      </Tooltip>
      <PopoverBox visible={measurementPopover}>
        <MeasurementContent />
      </PopoverBox>
      {showSmallToolContent && <SmallToolContent />}
    </>
  );
}
